<template>
  <base-dialog @cancel="cancelDialog">
    <div slot="header">{{ $t('common.create') }}</div>
    <div slot="body">
      <a-form-model
        ref="form"
        :model="form"
        :rules="rules"
        v-bind="formItemLayout">
        <a-form-model-item :label="$t('iamee.parent_organization')" prop="org_id">
          <a-tree-select
            v-model="form.org_id"
            tree-data-simple-mode
            style="width: 100%"
            showSearch
            :searchValue.sync="searchValue"
            :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
            :tree-data="params.treeData"
            :replaceFields="{value: 'keyValue'}"
            :placeholder="$t('common.tips.select', [$t('iamee.parent_organization')])" />
        </a-form-model-item>
        <a-form-model-item :label="$t('iamee.level')" required>
          <a-row :gutter="8">
            <a-col :span="12">
              <a-form-model-item prop="level_name" :colon="false" class="mb-0">
                <a-input
                  v-model="form.level_name"
                  :placeholder="$t('iamee.level_desc_placeholder')"
                  :disabled="!isLastLevel" />
              </a-form-model-item>
            </a-col>
            <a-col :span="12">
              <a-form-model-item prop="name" :colon="false" class="mb-0">
                <a-input
                  v-model="form.name"
                  :placeholder="$t('iamee.level_name_placeholder')" />
              </a-form-model-item>
            </a-col>
          </a-row>
        </a-form-model-item>
        <a-form-model-item :label="$t('iamee.weight')" prop="weight" :extra="$t('iamee.weight_extra')">
          <a-input-number v-model="form.weight" :min="1" :step="1" :precision="0" :placeholder="$t('common.tips.input', [$t('iamee.weight')])" style="width:100%" />
        </a-form-model-item>
        <a-form-model-item :label="$t('table.title.desc')" prop="description">
          <a-input v-model="form.description" :placeholder="$t('common.tips.input',[$t('table.title.desc')])" />
        </a-form-model-item>
      </a-form-model>
    </div>
    <div slot="footer">
      <a-button type="primary" @click="handleConfirm" :loading="loading">{{ $t('dialog.ok') }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'
import { validateModelForm } from '@/utils/validate'

export default {
  name: 'TreeNodeCreateDialog',
  mixins: [DialogMixin, WindowsMixin],
  data () {
    const { selectedOrgId } = this.params
    const { keys = '', level } = this.params.organization
    let levels = level && keys ? keys.replace(/\\\//g, '^^').split('/') : []
    levels = levels.map(key => key.replace(/\^\^/g, '/'))
    return {
      loading: false,
      levels,
      form: {
        org_id: selectedOrgId,
        level_name: '',
        name: '',
        weight: 1,
        description: '',
      },
      rules: {
        org_id: { required: true, message: this.$t('common.tips.select', [this.$t('iamee.parent_organization')]) },
        name: { required: true, message: this.$t('common.tips.input', [this.$t('table.title.name')]) },
        level_name: { required: true, message: this.$t('common.tips.input', [this.$t('iamee.level_desc')]) },
        weight: { required: true, message: this.$t('common.tips.input', [this.$t('iamee.weight')]) },
      },
      formItemLayout: {
        wrapperCol: {
          span: 20,
        },
        labelCol: {
          span: 4,
        },
      },
      searchValue: '',
    }
  },
  computed: {
    selectedParent () {
      const id = this.form.org_id
      if (!id) return null
      // tree-select 可能回传 keyValue / key / id，需兼容
      return this.params.nodeList.find(item => item.keyValue === id || item.key === id || item.id === id) || null
    },
    // 父节点已是组织最深层级（末端）时，新建需扩展新层级，层级描述可编辑；否则沿用已有层级描述不可改
    isLastLevel () {
      const parent = this.selectedParent
      if (!parent) return false
      if (parent.key === 'root') {
        return !this.params.nodeList.some(item => item.key !== 'root')
      }
      const parentLevel = Number(parent.level) || 0
      const orgLevel = Number(this.params.organization && this.params.organization.level) || this.levels.length
      return parentLevel >= orgLevel
    },
  },
  watch: {
    'form.org_id': {
      handler: function (val) {
        if (!val) return
        if (this.isLastLevel) {
          this.form.level_name = ''
        } else {
          const parent = this.selectedParent
          if (!parent) return
          const targetLevel = Number(parent.level) || 0
          this.form.level_name = this.levels[targetLevel] || ''
        }
      },
      immediate: true,
    },
  },
  destroyed () {
    this.manager = null
  },
  created () {
    this.manager = new this.$Manager('organizations', 'v1')
  },
  methods: {
    genParams (target) {
      const { name, level_name, description, weight } = this.form
      const { isLastLevel } = this
      const ret = { description, weight }
      if (isLastLevel) {
        ret.key = [level_name]
        const tags = {};
        (target.tags || []).map(item => {
          tags[item.key.replace('org:', '')] = item.value
        })
        tags[level_name] = name
        ret.tags = tags
      } else {
        const childs = this.params.nodeList.filter(item => item.parentFullLabel === target.fullLabel)
        if (childs[0]) {
          const key = childs[0].tagKey
          const tags = {};
          (target.tags || []).map(item => {
            tags[item.key.replace('org:', '')] = item.value
          })
          tags[key.replace('org:', '')] = name
          ret.tags = tags
        } else {
          const tags = {}
          target.tags.map(item => {
            tags[item.key.replace('org:', '')] = item.value
          })
          tags[level_name] = name
          ret.tags = tags
        }
      }
      return ret
    },
    async handleConfirm () {
      this.loading = true
      try {
        await validateModelForm(this.$refs.form)
        const parent = this.selectedParent
        if (!parent) {
          this.loading = false
          return
        }
        const isLastLevel = this.isLastLevel
        const data = this.genParams(parent)
        await this.manager.performAction({
          id: this.params.orgId,
          action: isLastLevel ? 'add-level' : 'add-node', // 末端层级后增加层级，否则增加节点
          data: data,
        })
        this.loading = false
        this.params.success()
        this.cancelDialog()
      } catch (error) {
        this.loading = false
        throw error
      }
    },
  },
}
</script>
